/////////////////////////////////////////////////////////////////////////////////////////////
// Copyright 2026 Garmin International, Inc.
// Licensed under the Flexible and Interoperable Data Transfer (FIT) Protocol License; you
// may not use this file except in compliance with the Flexible and Interoperable Data
// Transfer (FIT) Protocol License.
/////////////////////////////////////////////////////////////////////////////////////////////
// ****WARNING****  This file is auto-generated!  Do NOT edit this file.
// Profile Version = 21.218.0Release
// Tag = production/release/21.218.0-0-g96fee6d7
/////////////////////////////////////////////////////////////////////////////////////////////

package com.garmin.fit

import com.garmin.fit.types.*
import kotlin.time.Instant

/**
 * The FIT `gps_metadata` message.
 *
 * A thin typed view over [Mesg]: the accessors below read and write the same
 * numbered fields the untyped API exposes, so nothing is hidden by using them.
 */
public open class GpsMetadataMesg : Mesg {
    public constructor() : super("GpsMetadata", Profile.MesgNum.GPS_METADATA)

    public constructor(mesg: Mesg) : super(mesg)

    internal constructor(mesg: Mesg, adopt: Boolean) : super(mesg, adopt)

    /** timestamp, in s. Whole second part of the timestamp. */
    public var timestamp: Instant?
        get() = (getFieldValue(TIMESTAMP_FIELD_NUM) as? UInt)?.let { FitDateTime.toInstant(it) }
        set(value) = setFieldValue(TIMESTAMP_FIELD_NUM, value?.let { FitDateTime.toTimestamp(it) })

    /** timestamp_ms, in ms. Millisecond part of the timestamp. */
    public var timestampMs: UShort?
        get() = getFieldValue(TIMESTAMP_MS_FIELD_NUM) as? UShort
        set(value) = setFieldValue(TIMESTAMP_MS_FIELD_NUM, value)

    /** position_lat, in semicircles. */
    public var positionLat: Int?
        get() = getFieldValue(POSITION_LAT_FIELD_NUM) as? Int
        set(value) = setFieldValue(POSITION_LAT_FIELD_NUM, value)

    /** position_long, in semicircles. */
    public var positionLong: Int?
        get() = getFieldValue(POSITION_LONG_FIELD_NUM) as? Int
        set(value) = setFieldValue(POSITION_LONG_FIELD_NUM, value)

    /** enhanced_altitude, in m. */
    public var enhancedAltitude: Double?
        get() = getFieldValue(ENHANCED_ALTITUDE_FIELD_NUM) as? Double
        set(value) = setFieldValue(ENHANCED_ALTITUDE_FIELD_NUM, value)

    /** enhanced_speed, in m/s. */
    public var enhancedSpeed: Double?
        get() = getFieldValue(ENHANCED_SPEED_FIELD_NUM) as? Double
        set(value) = setFieldValue(ENHANCED_SPEED_FIELD_NUM, value)

    /** heading, in degrees. */
    public var heading: Double?
        get() = getFieldValue(HEADING_FIELD_NUM) as? Double
        set(value) = setFieldValue(HEADING_FIELD_NUM, value)

    /** utc_timestamp, in s. Used to correlate UTC to system time if the timestamp of the message is in system time. This UTC time is derived from the GPS data. */
    public var utcTimestamp: Instant?
        get() = (getFieldValue(UTC_TIMESTAMP_FIELD_NUM) as? UInt)?.let { FitDateTime.toInstant(it) }
        set(value) = setFieldValue(UTC_TIMESTAMP_FIELD_NUM, value?.let { FitDateTime.toTimestamp(it) })

    /** Number of values stored in velocity. */
    public val numVelocity: Int
        get() = getField(VELOCITY_FIELD_NUM)?.numValues ?: 0

    /** velocity, in m/s. velocity[0] is lon velocity. Velocity[1] is lat velocity. Velocity[2] is altitude velocity. */
    public var velocity: List<Double?>?
        get() {
            val field = getField(VELOCITY_FIELD_NUM) ?: return null
            return List(field.numValues) { getVelocity(it) }
        }
        set(value) {
            removeField(VELOCITY_FIELD_NUM)
            value?.forEachIndexed { index, element -> setVelocity(index, element) }
        }

    public fun getVelocity(index: Int): Double? =
        getFieldValue(VELOCITY_FIELD_NUM, index) as? Double

    public fun setVelocity(index: Int, value: Double?) {
        setFieldValue(VELOCITY_FIELD_NUM, value, index)
    }

    public companion object {
        public const val TIMESTAMP_FIELD_NUM: UByte = 253u
        public const val TIMESTAMP_MS_FIELD_NUM: UByte = 0u
        public const val POSITION_LAT_FIELD_NUM: UByte = 1u
        public const val POSITION_LONG_FIELD_NUM: UByte = 2u
        public const val ENHANCED_ALTITUDE_FIELD_NUM: UByte = 3u
        public const val ENHANCED_SPEED_FIELD_NUM: UByte = 4u
        public const val HEADING_FIELD_NUM: UByte = 5u
        public const val UTC_TIMESTAMP_FIELD_NUM: UByte = 6u
        public const val VELOCITY_FIELD_NUM: UByte = 7u

        /**
         * The profile definition of every field of this message, with no values.
         *
         * Built once when this class is first touched, and handed out only as
         * copies, so the decoder pays for a message type it never meets nothing.
         */
        internal val PROTOTYPE_FIELDS: Map<Int, Field> = buildPrototypeFields()

        private fun buildPrototypeFields(): Map<Int, Field> {
            val fields = LinkedHashMap<Int, Field>()
            fields[253] = Field("Timestamp", 253u, BaseType.UINT32, 1.0, 0.0, "s", false)
            fields[0] = Field("TimestampMs", 0u, BaseType.UINT16, 1.0, 0.0, "ms", false)
            fields[1] = Field("PositionLat", 1u, BaseType.SINT32, 1.0, 0.0, "semicircles", false)
            fields[2] = Field("PositionLong", 2u, BaseType.SINT32, 1.0, 0.0, "semicircles", false)
            fields[3] = Field("EnhancedAltitude", 3u, BaseType.UINT32, 5.0, 500.0, "m", false)
            fields[4] = Field("EnhancedSpeed", 4u, BaseType.UINT32, 1000.0, 0.0, "m/s", false)
            fields[5] = Field("Heading", 5u, BaseType.UINT16, 100.0, 0.0, "degrees", false)
            fields[6] = Field("UtcTimestamp", 6u, BaseType.UINT32, 1.0, 0.0, "s", false)
            fields[7] = Field("Velocity", 7u, BaseType.SINT16, 100.0, 0.0, "m/s", false)
            return fields
        }
    }
}
